/**
 * Clipboard, both directions.
 *
 * The rule for both functions: the control is never dead. `readText` is
 * gated behind a permission prompt in Chromium, a paste popup in Safari and a
 * paste-button confirmation in Firefox, and any of the three can be declined
 * or blocked outright. When it is, the Paste button falls back to focusing and
 * selecting the field — which is not "nothing happened", it is the state from
 * which the platform's own paste gesture works, on every device including the
 * iOS long-press menu.
 *
 * Both functions must be called synchronously from inside a user gesture. The
 * clipboard APIs are gesture-gated, and awaiting anything before the call is
 * how the gesture is lost.
 */

export type PasteOutcome = 'pasted' | 'focused';

/**
 * Put the clipboard's text into an input.
 *
 * Returns 'pasted' when the text was read and inserted, 'focused' when the
 * caller should let the user paste themselves. The input is focused in both
 * cases, so the two outcomes differ only in whether text arrived.
 */
export async function pasteInto(input: HTMLInputElement): Promise<PasteOutcome> {
  const clipboard = navigator.clipboard as Clipboard | undefined;
  if (!clipboard || typeof clipboard.readText !== 'function') {
    input.focus();
    input.select();
    return 'focused';
  }
  // readText() FIRST, before anything that could yield. Safari drops transient
  // activation across an await, and a focus() call is cheap but a habit of
  // putting work in front of the clipboard call is not.
  //
  // Feature detection is not enough on its own either: Android WebView (the
  // in-app browsers inside Instagram, Facebook and TikTok — which is exactly
  // the traffic this site expects) exposes readText and then fails. So the
  // button is rendered unconditionally and degrades at click time.
  const pending = clipboard.readText();
  input.focus();
  try {
    const text = await pending;
    if (typeof text !== 'string' || text.trim() === '') {
      input.select();
      return 'focused';
    }
    input.value = text.trim();
    // Fire the events a human typing would, so the platform-detection
    // listener runs without a special case for programmatic input.
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    return 'pasted';
  } catch {
    // Denied, blocked, or unavailable in this context. All three end the same
    // way: the user pastes, we get out of the way.
    input.select();
    return 'focused';
  }
}

/**
 * Copy text. Returns true when it landed.
 *
 * `writeText` is called without awaiting anything first, then the deprecated
 * `execCommand` path covers the engines and contexts where it is unavailable.
 * Keeping the legacy path is not nostalgia: it is the only copy mechanism in a
 * non-secure context and in a handful of embedded webviews, and it costs
 * fifteen lines.
 */
export async function copyText(text: string): Promise<boolean> {
  const clipboard = navigator.clipboard as Clipboard | undefined;
  if (clipboard && typeof clipboard.writeText === 'function') {
    try {
      await clipboard.writeText(text);
      return true;
    } catch {
      /* fall through */
    }
  }
  return legacyCopy(text);
}

function legacyCopy(text: string): boolean {
  const area = document.createElement('textarea');
  area.value = text;
  // Off-screen but focusable, and readonly so a mobile keyboard does not open.
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.top = '-1000px';
  area.style.opacity = '0';
  document.body.append(area);
  try {
    area.select();
    area.setSelectionRange(0, area.value.length);
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    area.remove();
  }
}
